import ky, { type KyInstance } from "ky";
import { withAdminBase } from "@/app/base-path";
import { refreshStaffSession } from "@/features/auth/api/auth-staff";
import {
	applyStaffAccess,
	clearSession,
	getAccessToken,
	getRefreshToken,
} from "@/features/auth/lib/auth-store";
import { getApiBaseUrl } from "@/shared/api/config";

let refreshInFlight: Promise<boolean> | null = null;

async function tryRefreshStaffAccess(): Promise<boolean> {
	const refreshToken = getRefreshToken();
	if (!refreshToken) {
		return false;
	}
	if (!refreshInFlight) {
		refreshInFlight = refreshStaffSession(refreshToken)
			.then((next) => {
				applyStaffAccess(next);
				return true;
			})
			.catch(() => false)
			.finally(() => {
				refreshInFlight = null;
			});
	}
	return refreshInFlight;
}

function bounceToLogin(): void {
	clearSession();
	const loginPath = withAdminBase("/login");
	if (window.location.pathname !== loginPath) {
		window.location.assign(loginPath);
	}
}

export const api: KyInstance = ky.create({
	prefixUrl: getApiBaseUrl(),
	hooks: {
		beforeRequest: [
			(request) => {
				const token = getAccessToken();
				if (token) {
					request.headers.set("Authorization", `Bearer ${token}`);
				}
			},
		],
		afterResponse: [
			async (request, options, response) => {
				if (response.status !== 401) {
					return response;
				}

				if (getRefreshToken()) {
					const refreshed = await tryRefreshStaffAccess();
					if (refreshed) {
						const token = getAccessToken();
						const retry = new Request(request, options as RequestInit);
						if (token) {
							retry.headers.set("Authorization", `Bearer ${token}`);
						}
						return ky(retry);
					}
				}

				bounceToLogin();
				return response;
			},
		],
	},
});

export type ApiErrorBody = {
	statusCode?: number;
	message?: string | string[];
	error?: string;
	code?: string;
	details?: { session?: string };
};

export async function readApiError(response: Response): Promise<ApiErrorBody> {
	try {
		return (await response.json()) as ApiErrorBody;
	} catch {
		return { statusCode: response.status, message: response.statusText };
	}
}
